import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloudUploadOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Form, Image, Input, Row, Select } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ClearBlogLink,
  DeleteBlogAction,
  GetBlogByLinkAction,
  SaveBlogAction,
  StoreAction,
} from '../../../app/ActionsImpl';
import { AppState } from '../../../app/AppState';
import { Blog, BlogCategory, TopBlogEnum } from '../../../app/ResponseInterfaces';
import { Option } from '../../../components/Input';
import { DocumentEditor } from '../../editor/components/document-editor/DocumentEditor';

export interface Props extends StateProps, DispatchProps {
  isLoading: boolean;
  refreshBlog: () => void;
}

const BlogEditorPost = (props: Props) => {
  const [currentBlog, setCurrentBlog] = useState<Blog>();
  const [isBlogModalVisible, setIsBlogModalVisible] = useState(false);

  const [editorReference, setEditorReference] = useState<any>();
  const [blogDescription, setBlogDescription] = useState('');
  const [blogMetaDescription, setBlogMetaDescription] = useState('');
  const [blogLink, setBlogLink] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogMetaTitle, setBlogMetaTitle] = useState('');
  const [linkValid, setLinkValid] = useState(true);

  useEffect(() => {
    if (props.blog) {
      setCurrentBlog(props.blog);
      if (props.blog.description) {
        setBlogDescription(props.blog.description);
      } else {
        setBlogDescription('');
      }
      if (props.blog.metaDescription) {
        setBlogMetaDescription(props.blog.metaDescription);
      } else {
        setBlogMetaDescription('');
      }
      if (props.blog.content) {
        setBlogContent(props.blog.content);
      } else {
        setBlogContent('');
      }
      if (props.blog.metaTitle) {
        setBlogMetaTitle(props.blog.metaTitle);
      } else {
        setBlogMetaTitle('');
      }
      if (props.blog.link) {
        setBlogLink(props.blog.link);
      } else {
        setBlogLink('');
      }
      setLinkValid(true);
    } else {
      setLinkValid(true);
    }
  }, [props.blog]);

  useEffect(() => {
    if (props.blogLink) {
      if (props.blog) {
        if (props.blog._id === props.blogLink._id) {
          setLinkValid(true);
        } else {
          setLinkValid(false);
        }
      } else {
        setLinkValid(true);
      }
    } else {
      setLinkValid(true);
    }
    // const re = /^[a-z0-9-]+$/g;
    // setLinkValid(re.test(blogLink))
  }, [props.blogLink]);

  // Handle delete categories
  const showBlogModal = () => {
    setIsBlogModalVisible(true);
  };

  const handleOk = () => {
    setIsBlogModalVisible(false);
    if (currentBlog?._id) {
      handleDeleteBlog(currentBlog?._id);
    }
  };

  const handleCancel = () => {
    setIsBlogModalVisible(false);
  };

  const handleDeleteBlog = (id: string) => {
    props.deleteBlog(id);
    props.refreshBlog();
  };

  const getCategoryIndex = (
    categoryId: string,
    categories: BlogCategory[],
  ): number => {
    let i;
    for (i = 0; i < categories.length; i++) {
      if (categories[i]._id === categoryId) {
        return i;
      }
    }
    return 0;
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentBlog({
      title: 'Title new ...',
      ...currentBlog,
      [event.target.name]: event.target.value,
    });
  };

  const onSelectChange = (categoryId: string) => {
    setCurrentBlog({
      title: '',
      ...currentBlog,
      blogCategoryId: categoryId,
    });
  };

  const onSelectTopBlogChange = (newTopBlog: TopBlogEnum) => {
    setCurrentBlog({
      title: '',
      ...currentBlog,
      topBlog: newTopBlog,
    });
  };

  function myUndo() {
    let myEditor = editorReference.getEditor();
    myEditor.history.undo();
  }

  function myRedo() {
    let myEditor = editorReference.getEditor();
    myEditor.history.redo();
  }

  function printPreview() {
    window.print();
  }

  const modules = useMemo(
    () => ({
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: false,
      },
      // mentions: entions.current,
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],

          [{ color: [] }, { background: [] }],

          [{ align: [] }],
          ['undo', 'redo'],
          ['printPreview'],

          ['clean'],
        ],
        handlers: {
          undo: myUndo,
          redo: myRedo,
          printPreview: printPreview,
        },
      },
      clipboard: {
        matchVisual: false,
      }
    }),
    editorReference,
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let tmpBlog = currentBlog;
    if (tmpBlog) {
      tmpBlog.description = blogDescription;
      tmpBlog.metaDescription = blogMetaDescription;
      tmpBlog.content = blogContent;
      tmpBlog.metaTitle = blogMetaTitle;
      tmpBlog.link = blogLink;
    }
    setCurrentBlog({
      title: '',
      ...tmpBlog,
    });
    updateBlog(currentBlog);
  };

  const updateBlog = (blog?: Blog) => {
    if (blog) {
      props.saveBlog(blog);
    }
    props.refreshBlog();
  };

  const onDescriptionChange = (html: any) => {
    setBlogDescription(html);
  };

  const onMetaDescriptionChange = (e: any) => {
    setBlogMetaDescription(e.target.value);
  };

  const onLinkChange = (e: any) => {
    setBlogLink(e.target.value);
  };

  const onContentChange = (html: any) => {
    setBlogContent(html);
  };

  const onMetaTitleChange = (e: any) => {
    setBlogMetaTitle(e.target.value);
  };

  const handleFileChange = async (event: any) => {
    let files2Upload: any[] = [];
    for (let oneFile of event.target.files) {
      let reader = new FileReader();
      reader.onload = async (loadEvent: any) => {
        let tmp1 = {
          name: oneFile.name,
          content: reader.result,
        };
        files2Upload.push(tmp1);

        if (files2Upload.length === event.target.files.length) {
          let tmpBlog: Blog = {
            title: 'Photo title',
            ...currentBlog,
            photo: {
              name: files2Upload[0].name,
              content: files2Upload[0].content,
            },
          };

          if (tmpBlog?.photo) {
            setCurrentBlog(tmpBlog);
          }
        }
      };
      await reader.readAsDataURL(oneFile);
    }
  };

  const checkBlogLink = () => {
    props.getBlogByLink(blogLink);
  };

  return (
    (props.isLoading && <p> N A H R A V A M </p>) ||
    (currentBlog && (
      <>
        <div className="site-card-wrapper">
          <Form className="orderFormLayout" onSubmitCapture={onSubmit}>
            <Row
              style={{
                justifyContent: 'space-between',
                border: '1px solid #f0f0f0',
              }}>
              <Col span={12}>
                <Card>
                  <b style={{ fontSize: 17, marginBottom: 15, display: 'block' }}>
                    Blog:
                  </b>
                  <Row>
                    <Col span={11}>
                      <Form.Item label="Kategória" style={{ alignItems: 'center' }}>
                        <Select
                          defaultValue={
                            (currentBlog.blogCategoryId &&
                              props.blogCategories &&
                              props.blogCategories[
                                getCategoryIndex(
                                  currentBlog.blogCategoryId,
                                  props.blogCategories,
                                )
                              ].title) ||
                            ''
                          }
                          style={{ width: 120 }}
                          onChange={(e) => onSelectChange(e)}
                          value={currentBlog.blogCategoryId}>
                          {props.blogCategories &&
                            props.blogCategories.map((c, ord) => (
                              <Option
                                key={ord}
                                value={c._id ? c._id : 'no-id-defined'}>
                                {c.title}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item label="Top Blog" style={{ alignItems: 'center' }}>
                        <Select
                          defaultValue={TopBlogEnum.None}
                          style={{ width: 120 }}
                          onChange={(e) => onSelectTopBlogChange(e)}
                          value={currentBlog.topBlog}>
                          {Object.entries(TopBlogEnum).map((e) => (
                            <Option key={e[0]} value={e[1]}>
                              {e[0]}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Nadpis" className="blogLabel">
                        <Input
                          value={
                            currentBlog.title === null
                              ? 'Nevyplnené'
                              : currentBlog.title
                          }
                          name="title"
                          onChange={onChange}
                          placeholder="Používať hlavné kľúčové slovo, na ktoré by sa mala webová stránka rankovať."
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="Title(Meta Tag)"
                        className="blogLabel">
                        <Input
                          value={blogMetaTitle}
                          name="Meta title"
                          onChange={onMetaTitleChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Popis(Meta Tag)" className="blogLabel">
                        <Input
                          value={blogMetaDescription}
                          name="Popis"
                          onChange={onMetaDescriptionChange}
                          maxLength={160}
                          placeholder="Max 160 znakov a mal by obsahovať aj hlavné kľúčové slovo."
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Link" className="blogLabel">
                        <Input
                          onBlur={checkBlogLink}
                          value={blogLink}
                          name="Link"
                          onChange={onLinkChange}
                          maxLength={160}
                          placeholder='Nesmie obsahovať špeciálne znaky, medzery ani "/"'
                          suffix={
                            blogLink !== '' ? (
                              linkValid ? (
                                <CheckCircleFilled
                                  style={{ color: '#0b0', fontSize: 25 }}
                                />
                              ) : (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                  }}>
                                  <CloseCircleFilled
                                    style={{ color: '#f00', fontSize: 25 }}
                                  />
                                  <span>Link už je použitý</span>
                                </div>
                              )
                            ) : (
                              <></>
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Aktívny od" className="blogLabel">
                        <Input
                          type="date"
                          value={
                            currentBlog.activeFrom === null
                              ? 'Nevyplnené'
                              : moment(currentBlog.activeFrom).format('YYYY-MM-DD')
                          }
                          name="activeFrom"
                          style={{ display: 'flex' }}
                          onChange={onChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Row>
                    <span
                      className="orderItemUploadIcons"
                      style={{ margin: '10px auto' }}>
                      <input
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept="image/jpg, image/jpeg"
                        id="image_upload_picker"
                        type="file"
                        multiple
                      />
                      <label
                        htmlFor="image_upload_picker"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 10,
                        }}>
                        <CloudUploadOutlined className="addButton importPicture" />
                        <b>Vložte náhľadový obrázok</b>
                      </label>
                    </span>
                    <Form.Item label="Názov obrázka" className="blogLabel">
                      <Input
                        value={
                          currentBlog.photo?.name === null
                            ? 'Nevyplnené'
                            : currentBlog.photo?.name
                        }
                        placeholder="Ako názov obrázka je vhodné použiť aj kľúčové slová."
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontWeight: 'bold', margin: '-20px 10px 20px 0', color: '#f00' }}>Vyberte obrázok do veľkosti max: 500 kB</div>
                      {currentBlog.photo?.content ? (
                        <div style={{ fontWeight: 'bold', display: 'flex', marginTop: '-10px' }}>
                          <div style={{ margin: '0 5px 20px 0' }}>
                            Veľkosť vybraného obrázka:{' '}
                          </div>
                          <span>
                            {((currentBlog.photo!.content.length * (3 / 4) - 2) / 1000).toFixed(0)
                              + ' kB'}
                          </span>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                    <Form.Item style={{ textAlign: 'center' }}>
                      <Image
                        width={300}
                        height={200}
                        alt={
                          currentBlog.photo?.name === undefined
                            ? 'Nevyplnené'
                            : currentBlog.photo?.name
                        }
                        src={
                          currentBlog.photo?.content === undefined
                            ? 'Error'
                            : currentBlog.photo?.content
                        }
                        fallback="data:image/png;base64,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"
                      />
                    </Form.Item>
                  </Row>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <h2 style={{ marginTop: 40, display: 'block' }}>
                  Táto sekcia slúži len ako krátky popis článku, resp. úvod článku,
                  pod nadpis.
                </h2>
                <DocumentEditor
                  theme="snow"
                  value={blogDescription}
                  onChange={onDescriptionChange}
                  ref={(el) => {
                    el && setEditorReference(el);
                  }}
                  defaultValue={blogDescription}
                  style={{ minHeight: '200px' }}
                  modules={modules}
                />
                <h2 style={{ marginTop: 40, display: 'block' }}>
                  Toto je hlavná časť, sem vložte text článku alebo fotky.
                </h2>
                <DocumentEditor
                  theme="snow"
                  value={blogContent}
                  onChange={onContentChange}
                  ref={(el) => {
                    el && setEditorReference(el);
                  }}
                  defaultValue={blogContent}
                  style={{ minHeight: '200px' }}
                  modules={modules}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 40 }}>
              <Col span={12}>
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                  <Button
                    onClick={showBlogModal}
                    className="removeButton"
                    style={{ width: 200, height: 50, fontSize: 20 }}>
                    Zmazať
                  </Button>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                  <Button
                    htmlType="submit"
                    className="addButton"
                    disabled={!linkValid}
                    style={{ width: 200, height: 50, fontSize: 20 }}>
                    Uložiť
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Modal
                title="Potvrďte zmazanie blogu"
                visible={isBlogModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}>
                <p>Naozaj chcete zmazať blog?</p>
              </Modal>
            </Row>
          </Form>
        </div>
      </>
    )) || <></>
  );
};

const mapStateToProps = ({ appState }: { appState: AppState }) => ({
  blog: appState.blog,
  // blogTitles: appState.blogTitles,
  blogCategories: appState.blogCategories,
  blogLink: appState.blogLink,
});

interface DispatchProps {
  saveBlog: (blog: Blog) => void;
  deleteBlog: (id: string) => void;
  getBlogByLink: (link: string) => void;
  clearBlogLink: () => void;
}

function mapDispatchToProps(dispatch: Dispatch<StoreAction, any>): DispatchProps {
  return {
    saveBlog: (blog: Blog) => dispatch(SaveBlogAction(blog)),
    deleteBlog: (id: string) => dispatch(DeleteBlogAction(id)),
    getBlogByLink: (link: string) => dispatch(GetBlogByLinkAction(link)),
    clearBlogLink: () => dispatch(ClearBlogLink()),
  };
}

type StateProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(BlogEditorPost);
